/**
 * API-KLIENTEN.
 *
 * DEN IMPORTERAR KONTRAKTET, DEN AVSKRIVER DET INTE.
 * Sökvägen, metoden, behörighetsnivån, rollerna, idempotensen och låsläget
 * läses UR RUTTOBJEKTET i shared/routes.ts vid varje anrop. Sökvägsbyggandet
 * nedan är samma segmentvisa uppbyggnad som worker/src/router/route-table.ts
 * resolvePath gör åt andra hållet. Ändras en sökväg i kontraktet ändras den
 * här samtidigt; ändras en TYP blir det ett kompileringsfel på anropsplatsen.
 * Det är hela skälet till att klienten läser shared/ direkt.
 *
 * TRE SAKER ANROPAREN MÅSTE VETA
 *
 *   1. VARJE ANROP KRÄVER EN GRANSKARE. Tredje argumentet är
 *      `(ravarde: unknown) => TSvar` och det går inte att utelämna. Se
 *      api/granska.ts för varför: ett `as TSvar` på en JSON.parse ger typerna
 *      utan att något prövats, och kontraktet förbjuder det uttryckligen.
 *
 *   2. OP_ID ÄR ANROPARENS, INTE KLIENTENS. Bär rutten idempotency 'op_id'
 *      MÅSTE du skicka ett — och SAMMA vid en omsändning. Det är hela poängen
 *      (beslut 09): samma op_id med samma innehåll spelar upp det gamla svaret
 *      i stället för att göra saken två gånger. Ett op_id som klienten hittade
 *      på vid varje försök hade gjort varje omsändning till en ny skrivning.
 *      Mynta ETT med nyUlid() när användaren trycker, spara det med det du ska
 *      skicka, och använd det tills anropet lyckats.
 *
 *   3. BEVISET SKÖTS HÄR. Bevisvalvet fylls automatiskt ur varje svar som bär
 *      ett färskt bevis, och UNAUTHENTICATED/LICENSE_EXPIRED rensar valvet och
 *      ropar `vidUtloggning`. Ingen skärm ska röra Authorization-huvudet.
 *
 * RUTTER MED auth 'cron' GÅR INTE ATT ANROPA härifrån. De ligger under
 * /api/internal/ bakom en Worker-hemlighet och är serverns egna. Ett försök
 * kastar — det är en bugg i anroparens kod, inte ett läge användaren kan hamna
 * i, och ett tyst Resultat hade dolt det.
 */

import { FILE_DOWNLOAD } from '@delat/routes';
import type { AuthLevel, Route } from '@delat/routes';
import type { ErrorDetailFor } from '@delat/errors';
import {
  AUTH_HEADER,
  AUTH_SCHEME,
  MAX_UPLOAD_BYTES,
  OP_ID_HEADER,
} from '@delat/types';
import type {
  AccountProof,
  CompanyId,
  LicenseProof,
  ObjectKey,
  OpId,
} from '@delat/types';
import { Granskningsfel } from './granska';
import type { Granskare } from './granska';
import {
  apifel,
  arKandFelkod,
  ejByggtfel,
  kraverOmloggning,
  lyckades,
  misslyckades,
  natfel,
  obegripligtfel,
} from './fel';
import type { Klartidfel, Resultat } from './fel';
import { valvet } from './bevis';
import type { Bevisvalv } from './bevis';

export type Sokvagsparametrar = Readonly<Record<string, string>>;

export interface Anropsval<TBegaran> {
  /** :company_id, :account_id, :object_key — samma namn som i route.path. */
  readonly params?: Sokvagsparametrar;
  /** Kroppen eller queryn. Vilket avgörs av route.request_in, aldrig av dig. */
  readonly data?: TBegaran;
  /** Obligatoriskt när route.idempotency är 'op_id'. Se punkt 2 i filhuvudet. */
  readonly op_id?: OpId;
  /** Avbryt anropet (t.ex. när skärmen lämnas). */
  readonly signal?: AbortSignal;
}

export interface Klientval {
  /**
   * Adressen till workern. Tom sträng = samma origin, vilket är det normala:
   * vite.config.ts proxar /api till wrangler dev, och i drift ligger PWA:n och
   * workern på samma värddatornamn. Någon CORS-uppsättning finns därför inte i
   * workern, och en annan origin här skulle bara ge tysta preflight-fel.
   */
  readonly bas?: string;
  readonly valv?: Bevisvalv;
  /**
   * Ropas när servern sagt UNAUTHENTICATED eller LICENSE_EXPIRED. Valvet är då
   * REDAN rensat. Appen svarar med att visa inloggningsskärmen — beslut 12:
   * ingenting är förlorat, utkorgen ligger kvar och skickas oförändrad efter
   * inloggningen.
   */
  readonly vidUtloggning?: (fel: Klartidfel) => void;
}

/* --- Sökvägen ----------------------------------------------------------- */

/**
 * Speglar worker/src/router/route-table.ts resolvePath. Varje parameter
 * URL-kodas: :object_key BÄR SNEDSTRECK ('<company_id>/photo/<ulid>.jpg') och
 * måste vara ETT segment på nätet även när den är tre i R2. Routern delar
 * sökvägen FÖRST och avkodar segmentet SEDAN.
 */
export const byggSokvag = (monster: string, params: Sokvagsparametrar): string =>
  monster
    .split('/')
    .map((del) => {
      if (!del.startsWith(':')) return del;
      const namn = del.slice(1);
      const varde = params[namn];
      if (varde === undefined || varde === '') {
        throw new Error(`sökvägsparametern ${namn} saknas för ${monster}`);
      }
      return encodeURIComponent(varde);
    })
    .join('/');

const byggQuery = (data: unknown): string => {
  if (data === undefined || data === null) return '';
  const p = new URLSearchParams();
  for (const [nyckel, varde] of Object.entries(data as Record<string, unknown>)) {
    if (varde === undefined || varde === null) continue;
    p.set(nyckel, String(varde));
  }
  const s = p.toString();
  return s === '' ? '' : `?${s}`;
};

/* --- Bevis som går att förnya ------------------------------------------- */

const arLicensbevis = (v: unknown): v is LicenseProof =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as LicenseProof).token === 'string' &&
  typeof (v as LicenseProof).company_id === 'string' &&
  typeof (v as LicenseProof).expires_at === 'number';

const arKontobevis = (v: unknown): v is AccountProof =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as AccountProof).token === 'string' &&
  typeof (v as AccountProof).expires_at === 'number';

/* --- Klienten ----------------------------------------------------------- */

export class Klartidklient {
  readonly #bas: string;
  readonly #valv: Bevisvalv;
  readonly #vidUtloggning: (fel: Klartidfel) => void;

  constructor(val: Klientval = {}) {
    this.#bas = (val.bas ?? '').replace(/\/+$/, '');
    this.#valv = val.valv ?? valvet;
    this.#vidUtloggning = val.vidUtloggning ?? (() => undefined);
  }

  /**
   * ETT anrop mot EN rutt.
   *
   *     const svar = await klient.anrop(ME, {}, granskaMe);
   *     if (!svar.ok) return visaFel(svar.fel);
   *     visa(svar.varde.account.name);
   */
  async anrop<TBegaran, TSvar>(
    rutt: Route<TBegaran, TSvar>,
    val: Anropsval<TBegaran>,
    granska: Granskare<TSvar>,
  ): Promise<Resultat<TSvar>> {
    if (rutt.auth === 'cron') {
      throw new Error(
        `${rutt.method} ${rutt.path} har auth 'cron' och är serverns egen rutt. ` +
          'Den går inte att anropa från klienten.',
      );
    }
    if (rutt.idempotency === 'op_id' && val.op_id === undefined) {
      throw new Error(
        `${rutt.method} ${rutt.path} kräver X-Op-Id. Mynta ETT med nyUlid() och ` +
          'använd SAMMA vid varje omsändning — se api/klient.ts punkt 2.',
      );
    }

    const params = val.params ?? {};
    const sokvag = byggSokvag(rutt.path, params);
    const query = rutt.request_in === 'query' ? byggQuery(val.data) : '';

    const huvuden: Record<string, string> = {};
    const bevisfel = this.#satHuvuden(huvuden, rutt.auth, params);
    if (bevisfel !== null) return misslyckades(bevisfel);
    if (val.op_id !== undefined) huvuden[OP_ID_HEADER] = val.op_id;

    let kropp: string | undefined;
    if (rutt.request_in === 'body') {
      huvuden['content-type'] = 'application/json';
      kropp = JSON.stringify(val.data ?? {});
    }

    return this.#skicka(rutt, `${this.#bas}${sokvag}${query}`, huvuden, kropp, val.signal, granska);
  }

  /**
   * FILE_DOWNLOAD är den enda rutt vars svar inte är JSON (BinaryBody =
   * ArrayBuffer), så den får en egen metod i stället för en flagga på anrop().
   * Nedladdning fungerar i låsläge — export ligger aldrig bakom betalvägg
   * (beslut 11).
   */
  async hamtaFil(
    firma: CompanyId,
    nyckel: ObjectKey,
    signal?: AbortSignal,
  ): Promise<Resultat<ArrayBuffer>> {
    const rutt = FILE_DOWNLOAD;
    const huvuden: Record<string, string> = {};
    const params = { company_id: firma, object_key: nyckel };
    const bevisfel = this.#satHuvuden(huvuden, rutt.auth, params);
    if (bevisfel !== null) return misslyckades(bevisfel);

    let svar: Response;
    try {
      svar = await fetch(`${this.#bas}${byggSokvag(rutt.path, params)}`, {
        method: rutt.method,
        headers: huvuden,
        ...(signal === undefined ? {} : { signal }),
      });
    } catch (orsak) {
      return misslyckades(natfel(orsak instanceof Error && orsak.name === 'AbortError'));
    }
    if (svar.ok) return lyckades(await svar.arrayBuffer());
    return misslyckades(await this.#tolkaFel(svar, `${rutt.method} ${rutt.path}`));
  }

  /**
   * FILE_UPLOAD skickar bytesen som JSON-kropp enligt FileUploadRequest.
   * Gränsen prövas HÄR också: MAX_UPLOAD_BYTES står i kontraktet, och en bild
   * som ändå avvisas av servern har redan kostat montören en uppladdning i
   * trapphuset.
   */
  overstigerFilgrans = (byteLangd: number): boolean => byteLangd > MAX_UPLOAD_BYTES;

  /* --- Innanmätet ------------------------------------------------------ */

  #satHuvuden(
    huvuden: Record<string, string>,
    auth: AuthLevel,
    params: Sokvagsparametrar,
  ): Klartidfel | null {
    if (auth === 'public') return null;

    if (auth === 'account') {
      const konto = this.#valv.las().konto;
      if (konto === null) return this.#loggaUt(apifel('UNAUTHENTICATED', undefined, ''));
      huvuden[AUTH_HEADER] = `${AUTH_SCHEME} ${konto.token}`;
      return null;
    }

    // auth 'company': beviset MÅSTE gälla firman i sökvägen.
    const firma = params['company_id'];
    if (firma === undefined) {
      throw new Error("en rutt med auth 'company' kräver params.company_id");
    }
    const licens = this.#valv.licensFor(firma as CompanyId);
    if (licens === null) return this.#loggaUt(apifel('UNAUTHENTICATED', undefined, ''));
    huvuden[AUTH_HEADER] = `${AUTH_SCHEME} ${licens.token}`;
    return null;
  }

  async #skicka<TSvar>(
    rutt: Route<unknown, TSvar>,
    adress: string,
    huvuden: Record<string, string>,
    kropp: string | undefined,
    signal: AbortSignal | undefined,
    granska: Granskare<TSvar>,
  ): Promise<Resultat<TSvar>> {
    const namn = `${rutt.method} ${rutt.path}`;
    let svar: Response;
    try {
      svar = await fetch(adress, {
        method: rutt.method,
        headers: huvuden,
        ...(kropp === undefined ? {} : { body: kropp }),
        ...(signal === undefined ? {} : { signal }),
      });
    } catch (orsak) {
      return misslyckades(natfel(orsak instanceof Error && orsak.name === 'AbortError'));
    }

    const text = await svar.text().catch(() => '');

    if (!svar.ok) return misslyckades(await this.#tolkaFel(svar, namn, text));

    let ratt: unknown;
    try {
      ratt = text === '' ? {} : JSON.parse(text);
    } catch {
      return misslyckades(obegripligtfel(svar.status, 'svaret var inte JSON'));
    }

    // BEVISET FÖRNYAS TYST (beslut 12) — före granskningen, så att en licens
    // som förnyades på ett svar vi inte förstod ändå hamnar i valvet.
    this.#absorberaBevis(ratt);

    try {
      return lyckades(granska(ratt));
    } catch (orsak) {
      if (orsak instanceof Granskningsfel) {
        console.error(`[klartid] ${namn} gav ett svar med fel form — ${orsak.message}`);
        return misslyckades(obegripligtfel(svar.status, orsak.message));
      }
      throw orsak;
    }
  }

  /**
   * Plockar ur bevis som kan följa med ett lyckat svar:
   *   SYNC_PUSH / SYNC_PULL / CREATE_COMPANY   { license: LicenseProof }
   *   ME / AUTH_VERIFY                          { account_proof, licenses }
   * Formen prövas lätt här; den GRANSKAS ordentligt av anropets egen granskare.
   * Det som inte känns igen lämnas orört.
   */
  #absorberaBevis(ratt: unknown): void {
    if (typeof ratt !== 'object' || ratt === null) return;
    const o = ratt as Record<string, unknown>;

    const licens = o['license'];
    if (arLicensbevis(licens)) this.#valv.fornyaLicens(licens);

    const licenser = o['licenses'];
    const kontobevis = o['account_proof'];
    if (arKontobevis(kontobevis)) {
      if (Array.isArray(licenser)) {
        for (const l of licenser) if (arLicensbevis(l)) this.#valv.fornyaLicens(l);
      }
      this.#valv.fornyaKonto(kontobevis);
    }
  }

  async #tolkaFel(svar: Response, namn: string, redanLast?: string): Promise<Klartidfel> {
    const text = redanLast ?? (await svar.text().catch(() => ''));
    let ratt: unknown;
    try {
      ratt = text === '' ? {} : JSON.parse(text);
    } catch {
      return obegripligtfel(svar.status, 'felsvaret var inte JSON');
    }
    const o = typeof ratt === 'object' && ratt !== null ? (ratt as Record<string, unknown>) : {};
    const servernsText = typeof o['message_sv'] === 'string' ? o['message_sv'] : '';

    // 501: rutten står i kontraktet men är inte byggd. Ingen ErrorCode betyder
    // det, så den får aldrig lånas en.
    if (svar.status === 501 && o['not_built'] === true) {
      const rutt = typeof o['route'] === 'string' ? o['route'] : namn;
      return ejByggtfel(rutt, servernsText);
    }
    if (svar.status === 404 && o['not_found'] === true) {
      console.error(`[klartid] ${namn}: routern känner inte sökvägen. Klientbugg.`);
      return { slag: 'saknas', text: 'Den här vägen finns inte i appen längre.' };
    }
    if (svar.status === 405 && o['method_not_allowed'] === true) {
      const tillatna = Array.isArray(o['allow']) ? o['allow'].map(String) : [];
      console.error(`[klartid] ${namn}: fel metod. Tillåtna: ${tillatna.join(', ')}. Klientbugg.`);
      return { slag: 'fel_metod', tillatna, text: 'Den här vägen finns inte i appen längre.' };
    }

    const kod = o['code'];
    if (!arKandFelkod(kod)) {
      return obegripligtfel(svar.status, `okänd felkod ${String(kod)}`);
    }
    // Koden är först känd vid körning, så detaljen kan inte prövas mot sin
    // kod-specifika form här. Det är bara TRANSPORTEN som får vara oprecis;
    // apifel() håller formen åt alla som BYGGER ett fel, och en skärm som
    // narrows på `code` får rätt form ut. Se ApifelFor i api/fel.ts.
    const fel = apifel(kod, o['detail'] as ErrorDetailFor<typeof kod>, servernsText);
    return kraverOmloggning(fel) ? this.#loggaUt(fel) : fel;
  }

  /**
   * Rensar valvet OCH ropar vidUtloggning. Ett bevis som servern sagt nej till
   * får inte ligga kvar och göra nästa anrop till samma nej.
   */
  #loggaUt(fel: Klartidfel): Klartidfel {
    this.#valv.rensa();
    this.#vidUtloggning(fel);
    return fel;
  }
}
